

// src/components/Invoice/invoiceApi.js
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Invoice API uses the AWS API Gateway endpoint
// Base URL as per API documentation
const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Helper function to make authenticated API requests
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    // Get authentication token
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error('No authentication token available');
    }

    // Trim token to remove any whitespace that might cause header parsing issues
    const token = idToken.trim();
    
    // Validate token format (should be a JWT)
    if (!token || token.length < 10) {
      throw new Error('Invalid authentication token format');
    }

    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }

    console.log(`Invoice API: Making ${method} request to: ${url}`);
    console.log('Invoice API: Authorization header format:', `Bearer ${token.substring(0, 20)}...`);
    if (body) {
      console.log('Invoice API: Request payload:', body);
    }

    let response;
    try {
      response = await fetch(url, options);
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
        const corsError = new Error('CORS Error: Unable to connect to the API. This may be a preflight (OPTIONS) request issue or backend CORS configuration issue.');
        corsError.isCorsError = true;
        corsError.isPreflightIssue = true;
        corsError.status = 0;
        corsError.method = method;
        corsError.endpoint = endpoint;
        console.error('Invoice API: CORS/Preflight Error Details:', {
          method,
          endpoint,
          url,
          error: fetchError.message,
          suggestion: 'Backend needs to handle OPTIONS requests and return proper CORS headers',
        });

        // Log CORS/network error to logging service
        loggingService.logError(
          'api_error_create_invoice',
          `Create Invoice API CORS/network error for ${endpoint}`,
          corsError,
          {
            endpoint,
            method,
            errorType: 'CORS/NetworkError',
            isCorsError: true,
            isPreflightIssue: true,
          }
        ).catch(logError => {
          console.warn('[invoiceApi] Failed to log error:', logError);
        });

        throw corsError;
      }

      // Log other fetch errors to logging service
      loggingService.logError(
        'api_error_create_invoice',
        `Create Invoice API network/fetch error for ${endpoint}`,
        fetchError,
        {
          endpoint,
          method,
          errorType: fetchError?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log error:', logError);
      });

      throw fetchError;
    }

    if (!response.ok) {
      // Read response as text first, then try to parse as JSON
      const errorText = await response.text().catch(() => '');
      let errorData = {};
      
      if (errorText) {
        try {
          errorData = JSON.parse(errorText);
        } catch {
          // If not JSON, use the text as the error message
          errorData = { message: errorText, error: errorText };
        }
      }
      
      console.error(
        `Invoice API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );
      console.error('Invoice API: Full error response:', errorText);
      console.error('Invoice API: Authorization header sent:', `Bearer ${token.substring(0, 20)}...`);

      // Extract error message from various possible response formats
      let errorMessage = 'Unknown error';
      if (errorData.body?.error) {
        errorMessage = errorData.body.error;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (typeof errorText === 'string' && errorText) {
        errorMessage = errorText; // Fallback to raw text if not parsable JSON
      }

      // Special handling for 403 Authorization errors
      if (response.status === 403 && errorMessage.includes('Authorization header')) {
        const authError = new Error(
          `Authentication Error (403): The invoice API endpoint may not be properly configured on the backend to accept the Authorization header format. ` +
          `This appears to be a backend configuration issue. Please contact the backend team to verify: ` +
          `1. The /billing/invoices endpoint is properly configured in API Gateway, ` +
          `2. The Cognito authorizer is correctly set up for this endpoint, ` +
          `3. The Authorization header format is being parsed correctly. ` +
          `Error details: ${errorMessage}`
        );
        authError.status = response.status;
        authError.statusText = response.statusText;
        authError.endpoint = endpoint;
        authError.responseData = errorData;
        authError.isAuthConfigError = true;

        // Log API error to logging service
        loggingService.logError(
          'api_error_create_invoice',
          `Create Invoice API error: ${response.status} ${response.statusText} for ${endpoint}`,
          authError,
          {
            endpoint,
            method,
            status: response.status,
            statusText: response.statusText,
            responseData: errorText,
            isAuthConfigError: true,
          }
        ).catch(logError => {
          console.warn('[invoiceApi] Failed to log error:', logError);
        });

        throw authError;
      }

      const error = new Error(`API Error: ${response.status} - ${errorMessage}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;

      // Log API error to logging service
      loggingService.logError(
        'api_error_create_invoice',
        `Create Invoice API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorText,
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log error:', logError);
      });

      throw error;
    }

    const data = await response.json();
    console.log('Invoice API: Response:', data);
    return data;
  } catch (error) {
    console.error(`Invoice API: Error in ${method} ${endpoint}:`, error);
    
    // Log authentication errors if not already logged
    if (error.message && error.message.includes('No authentication token')) {
      loggingService.logError(
        'api_error_create_invoice',
        `Create Invoice API authentication error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: 'AuthenticationError',
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log error:', logError);
      });
    }
    
    if (error.isCorsError) {
      throw error;
    }
    if (error.status) {
      throw error;
    }

    // Log other unhandled errors to logging service
    if (!error.isCorsError && !error.status) {
      loggingService.logError(
        'api_error_create_invoice',
        `Create Invoice API error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: error?.name || 'Error',
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log error:', logError);
      });
    }

    throw new Error(error.message || 'Network error');
  }
};

/**
 * Clean object by removing undefined, null, and empty string values
 */
const cleanObject = (obj) => {
  if (obj === null || obj === undefined) {
    return undefined;
  }
  if (Array.isArray(obj)) {
    return obj.map(cleanObject).filter((item) => item !== undefined);
  }
  if (typeof obj === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = cleanObject(value);
      if (cleanedValue !== undefined && cleanedValue !== null && cleanedValue !== '') {
        cleaned[key] = cleanedValue;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }
  return obj;
};

/**
 * Invoice API service
 */
export const invoiceApi = {
  /**
   * Create a new invoice
   * @param {Object} invoiceData - Invoice data including customer_id and lines
   * @returns {Promise<Object>} Created invoice with lines
   */
  createInvoice: async (invoiceData) => {
    try {
      const cleanedData = cleanObject(invoiceData);
      const response = await makeApiRequest('/billing/invoices', 'POST', cleanedData);
      
      // Log API event for creating invoice
      loggingService.logInfo(
        'api_success_invoice',
        `Successfully created invoice`,
        {
          endpoint: '/billing/invoices',
          method: 'POST',
          customerId: invoiceData.customer_id,
          linesCount: invoiceData.lines?.length || 0,
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        invoice: response.invoice,
        lines: response.lines || [],
      };
    } catch (error) {
      console.error('Invoice API: Error creating invoice:', error);
      return {
        success: false,
        error: error.message || 'Failed to create invoice',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * List invoices with optional filters
   * @param {Object} params - Query parameters (limit, status, customer_id, payment_status, site_id, start_date, end_date, search, last_key)
   * @returns {Promise<Object>} List of invoices with summary
   */
  listInvoices: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params.status) {
        queryParams.append('status', params.status);
      }
      if (params.customer_id) {
        queryParams.append('customer_id', params.customer_id);
      }
      if (params.payment_status) {
        queryParams.append('payment_status', params.payment_status);
      }
      if (params.site_id) {
        queryParams.append('site_id', params.site_id);
      }
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }
      if (params.last_key) {
        queryParams.append('last_key', params.last_key);
      }

      const queryString = queryParams.toString();
      const endpoint = `/billing/invoices${queryString ? `?${queryString}` : ''}`;

      const response = await makeApiRequest(endpoint, 'GET');
      
      // Log API event for listing invoices
      loggingService.logInfo(
        'api_success_invoice',
        `Successfully listed invoices`,
        {
          endpoint: '/billing/invoices',
          method: 'GET',
          invoicesCount: response.invoices?.length || 0,
          ...params,
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        invoices: response.invoices || [],
        summary: response.summary || {},
        last_key: response.last_key || null,
      };
    } catch (error) {
      console.error('Invoice API: Error listing invoices:', error);
      return {
        success: false,
        error: error.message || 'Failed to list invoices',
        status: error.status,
        responseData: error.responseData,
        invoices: [],
        summary: {},
      };
    }
  },

  /**
   * Get a single invoice by ID
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise<Object>} Invoice details with lines
   */
  getInvoice: async (invoiceId) => {
    try {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      const response = await makeApiRequest(`/billing/invoices/${invoiceId}`, 'GET');
      
      // Log API event for getting invoice
      loggingService.logInfo(
        'api_success_invoice',
        `Successfully fetched invoice ${invoiceId}`,
        {
          endpoint: `/billing/invoices/${invoiceId}`,
          method: 'GET',
          invoiceId,
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        invoice: response.invoice,
        lines: response.lines || [],
      };
    } catch (error) {
      console.error('Invoice API: Error getting invoice:', error);
      return {
        success: false,
        error: error.message || 'Failed to get invoice',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Update an invoice (partial updates supported)
   * @param {string} invoiceId - Invoice ID
   * @param {Object} updateData - Fields to update
   * @returns {Promise<Object>} Updated invoice
   */
  updateInvoice: async (invoiceId, updateData) => {
    try {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      const cleanedData = cleanObject(updateData);
      const response = await makeApiRequest(`/billing/invoices/${invoiceId}`, 'PUT', cleanedData);
      
      // Log API event for updating invoice
      loggingService.logInfo(
        'api_success_invoice',
        `Successfully updated invoice ${invoiceId}`,
        {
          endpoint: `/billing/invoices/${invoiceId}`,
          method: 'PUT',
          invoiceId,
          updatedFields: Object.keys(cleanedData || {}),
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        invoice: response.invoice,
        lines_updated: response.lines_updated || false,
      };
    } catch (error) {
      console.error('Invoice API: Error updating invoice:', error);
      return {
        success: false,
        error: error.message || 'Failed to update invoice',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Update invoice status
   * @param {string} invoiceId - Invoice ID
   * @param {string} status - New status (draft, sent, paid, overdue, cancelled)
   * @returns {Promise<Object>} Updated invoice status
   */
  updateInvoiceStatus: async (invoiceId, status) => {
    try {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      if (!status) {
        throw new Error('Status is required');
      }
      const response = await makeApiRequest(
        `/billing/invoices/${invoiceId}/status`,
        'POST',
        { status }
      );
      
      // Log API event for updating invoice status
      loggingService.logInfo(
        'api_success_invoice',
        `Successfully updated invoice status for ${invoiceId}`,
        {
          endpoint: `/billing/invoices/${invoiceId}/status`,
          method: 'POST',
          invoiceId,
          newStatus: status,
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        invoice: response.invoice,
        previous_status: response.previous_status,
        new_status: response.new_status,
      };
    } catch (error) {
      console.error('Invoice API: Error updating invoice status:', error);
      return {
        success: false,
        error: error.message || 'Failed to update invoice status',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Record a payment against an invoice
   * @param {string} invoiceId - Invoice ID
   * @param {Object} paymentData - Payment data (amount, payment_mode, payment_date, currency, reference_number, notes)
   * @returns {Promise<Object>} Payment record and updated invoice status
   */
  recordPayment: async (invoiceId, paymentData) => {
    try {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      const cleanedData = cleanObject(paymentData);
      const response = await makeApiRequest(
        `/billing/invoices/${invoiceId}/payments`,
        'POST',
        cleanedData
      );
      
      // Log API event for recording payment
      loggingService.logInfo(
        'api_success_invoice',
        `Successfully recorded payment for invoice ${invoiceId}`,
        {
          endpoint: `/billing/invoices/${invoiceId}/payments`,
          method: 'POST',
          invoiceId,
          amount: paymentData.amount,
          paymentMode: paymentData.payment_mode,
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        payment: response.payment,
        invoice_payment_status: response.invoice_payment_status,
        total_paid: response.total_paid,
        remaining: response.remaining,
      };
    } catch (error) {
      console.error('Invoice API: Error recording payment:', error);
      return {
        success: false,
        error: error.message || 'Failed to record payment',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Get presigned URL for invoice attachment upload
   * @param {string} invoiceId - Invoice ID
   * @param {Object} params - Query parameters (content_type, filename)
   * @returns {Promise<Object>} Presigned POST data and S3 URI
   */
  presignAttachmentUpload: async (invoiceId, params = {}) => {
    try {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      const queryParams = new URLSearchParams();
      if (params.content_type) {
        queryParams.append('content_type', params.content_type);
      }
      if (params.filename) {
        queryParams.append('filename', params.filename);
      }

      const queryString = queryParams.toString();
      const endpoint = `/billing/invoices/${invoiceId}/attachments/presign${queryString ? `?${queryString}` : ''}`;

      const response = await makeApiRequest(endpoint, 'POST');
      return {
        success: true,
        data: response,
        post_data: response.post_data,
        invoice_id: response.invoice_id,
        s3_uri: response.s3_uri,
        content_type: response.content_type,
      };
    } catch (error) {
      console.error('Invoice API: Error getting presigned URL:', error);
      return {
        success: false,
        error: error.message || 'Failed to get presigned URL',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Upload attachment to S3 using presigned POST
   * @param {Object} presignedPost - Presigned POST data from presignAttachmentUpload
   * @param {File} file - File to upload
   * @returns {Promise<Object>} Upload result
   */
  uploadAttachment: async (presignedPost, file) => {
    try {
      if (!presignedPost || !presignedPost.url || !presignedPost.fields) {
        throw new Error('Invalid presigned POST data');
      }
      if (!file) {
        throw new Error('File is required');
      }

      const formData = new FormData();
      Object.entries(presignedPost.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const response = await fetch(presignedPost.url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`S3 upload failed: ${response.status} - ${errorText}`);
      }

      return {
        success: true,
        message: 'Attachment uploaded successfully',
      };
    } catch (error) {
      console.error('Invoice API: Error uploading attachment:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload attachment',
      };
    }
  },

  /**
   * Generate invoice PDF
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise<Object>} PDF URL and metadata
   */
  generatePDF: async (invoiceId) => {
    try {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      const response = await makeApiRequest(`/billing/invoices/${invoiceId}/pdf`, 'GET');
      
      // Log API event for generating PDF
      loggingService.logInfo(
        'api_success_invoice',
        `Successfully generated PDF for invoice ${invoiceId}`,
        {
          endpoint: `/billing/invoices/${invoiceId}/pdf`,
          method: 'GET',
          invoiceId,
        }
      ).catch(logError => {
        console.warn('[invoiceApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        pdf_url: response.pdf_url,
        s3_key: response.s3_key,
        expires_in_seconds: response.expires_in_seconds,
        invoice_id: response.invoice_id,
        invoice_number: response.invoice_number,
      };
    } catch (error) {
      console.error('Invoice API: Error generating PDF:', error);
      return {
        success: false,
        error: error.message || 'Failed to generate PDF',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },
};

export default invoiceApi;

